// i18next-extract-mark-ns-start protection-policy-attachment

import {AnchorLink} from 'components/AnchorLink';
import {Article} from 'components/Article';
import {SEO} from 'components/SEO';
import {graphql} from 'gatsby';
import {Trans, useI18next} from 'gatsby-plugin-react-i18next';
import React from 'react';

const Page: React.FC = () => {
  const {t} = useI18next();
  return (
    <Article wide>
      <SEO
        path="protection-policy-attachment"
        title={t('Protection Policy Attachment')}
        description={t('Customer data protection policy (businesses and end users) - Attachment.')}
      />
      <div>
        <Trans parent="h1">Annex I</Trans>
        <Trans parent="h2">Information on merchant risk analysis</Trans>

        <div className="table-container">
          <table>
            <tbody>
              <tr>
                <td>
                  <Trans parent="strong">
                    Data categories used or to be used in the development of these models
                  </Trans>
                </td>
                <td>
                  <Trans>
                    Data provided directly by the client (merchant), as well as data obtained from
                    external sources, such as public records (for example, Commercial Registry, Tax
                    Agency) and other information sources, including social media or internet
                    searches. This data includes, among others, business description, legal address,
                    credit history, commercial references, and any other information relevant to the
                    analysis.
                  </Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans parent="strong">Why these categories are considered relevant</Trans>
                </td>
                <td>
                  <Trans>
                    The type of data used allows for a complete and accurate assessment of the risks
                    associated with the client. This data is fundamental to determine the financial
                    and reputational profile of the business, which in turn helps to develop a
                    scoring-based valuation model. This analysis aims to ensure the financial
                    viability of the commercial relationship and prevent possible economic or
                    reputational damage to MONEI.
                  </Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans parent="strong">How the models are developed</Trans>
                </td>
                <td>
                  <Trans>
                    The models are developed by evaluating the merchant category through scoring
                    systems that assign a numerical rating, generally on a scale of 0 to 100. These
                    models may include automated analysis processes.
                  </Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans parent="strong">
                    Why this model is relevant for automated decision-making
                  </Trans>
                </td>
                <td>
                  <Trans>
                    The relevance of this model lies in its ability to provide an objective,
                    uniform, and efficient assessment of the risks associated with each client. The
                    internal scoring models allow for reliable, fast, and data-based rating,
                    minimizing subjectivity and optimizing the decision-making process.
                  </Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans parent="strong">Expected consequences of this processing</Trans>
                </td>
                <td>
                  <Trans parent="p">
                    The processing of this data and the development of scoring models allow MONEI to
                    evaluate the client's financial capacity and suitability. As a result of the
                    analysis, an assessment is generated which, after review by the delegated body,
                    leads to one of the following decisions:
                  </Trans>
                  <ul>
                    <Trans parent="li">Approve the contract.</Trans>
                    <Trans parent="li">
                      Analyze the case further if there are doubts or inconclusive results.
                    </Trans>
                    <Trans parent="li">
                      Deny the contract if a high economic or reputational risk is identified.
                    </Trans>
                  </ul>
                  <Trans parent="p">
                    This process ensures that decisions are based on rigorous and fair analysis,
                    aligning with MONEI's objectives and current regulations.
                  </Trans>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Article>
  );
};

export default Page;

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(
      filter: {ns: {in: ["common", "protection-policy-attachment"]}, language: {eq: $language}}
    ) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
